import { db } from '../config/firebase';
import { doc, getDoc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';

/**
 * Serviço para gerenciar o status de assinatura integrado com Firebase Stripe Extension
 * 
 * A Firebase Stripe Extension cria automaticamente:
 * - Collection: customers/{userId}/subscriptions
 * - Atualizada via webhooks do Stripe
 * 
 * Status de subscription no Stripe:
 * - 'active': Assinatura ativa e paga
 * - 'trialing': Período de trial
 * - 'past_due': Pagamento atrasado (tentando cobrar)
 * - 'canceled': Cancelada pelo usuário
 * - 'unpaid': Não paga após tentativas
 * - 'incomplete': Pagamento inicial não completado
 */

export interface StripeSubscription {
  id: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'incomplete_expired';
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  created: number;
  metadata?: Record<string, any>;
  price?: {
    id: string;
    product: string;
    unit_amount: number;
    currency: string;
    recurring: {
      interval: 'month' | 'year';
      interval_count: number;
    };
    /** Presente nos Prices do site 2026 (solo / clinica / plus) */
    metadata?: Record<string, string>;
  };
}

export interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  subscription: StripeSubscription | null;
  isTrialing: boolean;
  isPastDue: boolean;
  daysUntilRenewal: number;
  nextBillingDate: Date | null;
}

class SubscriptionStatusService {
  /**
   * Verifica o status da assinatura do usuário no Firestore
   * (atualizado automaticamente pelos webhooks Stripe)
   */
  async checkSubscriptionStatus(userId: string): Promise<SubscriptionStatus> {
    console.log('🔍 [SUBSCRIPTION-STATUS] Verificando status para userId:', userId);

    try {
      // Buscar subscriptions na collection criada pela Firebase Stripe Extension
      const subscriptionsRef = collection(db, 'customers', userId, 'subscriptions');
      const subscriptionsSnapshot = await getDocs(subscriptionsRef);

      if (subscriptionsSnapshot.empty) {
        console.log('📭 [SUBSCRIPTION-STATUS] Nenhuma subscription encontrada');
        return {
          hasActiveSubscription: false,
          subscription: null,
          isTrialing: false,
          isPastDue: false,
          daysUntilRenewal: 0,
          nextBillingDate: null
        };
      }

      // Pegar a subscription mais recente
      const subscriptions: StripeSubscription[] = [];
      subscriptionsSnapshot.forEach((doc) => {
        subscriptions.push({ id: doc.id, ...doc.data() } as StripeSubscription);
      });

      // Ordenar por data de criação (mais recente primeiro)
      subscriptions.sort((a, b) => b.created - a.created);
      const latestSubscription = subscriptions[0];

      console.log('📋 [SUBSCRIPTION-STATUS] Subscription encontrada:', {
        id: latestSubscription.id,
        status: latestSubscription.status,
        current_period_end: new Date(latestSubscription.current_period_end * 1000).toLocaleDateString('pt-BR')
      });

      // Determinar se tem acesso ativo
      const activeStatuses: string[] = ['active', 'trialing'];
      const hasActiveSubscription = activeStatuses.includes(latestSubscription.status);
      const isTrialing = latestSubscription.status === 'trialing';
      const isPastDue = latestSubscription.status === 'past_due';

      // Calcular dias até renovação
      const now = Date.now() / 1000; // Timestamp em segundos
      const secondsUntilRenewal = latestSubscription.current_period_end - now;
      const daysUntilRenewal = Math.ceil(secondsUntilRenewal / (60 * 60 * 24));
      const nextBillingDate = new Date(latestSubscription.current_period_end * 1000);

      const status: SubscriptionStatus = {
        hasActiveSubscription,
        subscription: latestSubscription,
        isTrialing,
        isPastDue,
        daysUntilRenewal: Math.max(0, daysUntilRenewal),
        nextBillingDate
      };

      console.log('✅ [SUBSCRIPTION-STATUS] Status calculado:', status);

      return status;

    } catch (error) {
      console.error('❌ [SUBSCRIPTION-STATUS] Erro ao verificar status:', error);
      throw error;
    }
  }

  /**
   * Listener em tempo real para mudanças na subscription
   * Útil para atualizar UI quando webhook atualiza o status
   */
  subscribeToSubscriptionChanges(
    userId: string,
    callback: (status: SubscriptionStatus) => void
  ): () => void {
    console.log('👂 [SUBSCRIPTION-STATUS] Iniciando listener para:', userId);

    const subscriptionsRef = collection(db, 'customers', userId, 'subscriptions');

    const unsubscribe = onSnapshot(subscriptionsRef, async (snapshot) => {
      console.log('🔔 [SUBSCRIPTION-STATUS] Mudança detectada nas subscriptions');
      
      try {
        const status = await this.checkSubscriptionStatus(userId);
        callback(status);
      } catch (error) {
        console.error('❌ [SUBSCRIPTION-STATUS] Erro no listener:', error);
      }
    });

    return unsubscribe;
  }

  /**
   * Verifica se o usuário pode acessar o sistema
   * Combina verificação de trial + subscription Stripe
   */
  async canAccessSystem(userId: string, trialExpired: boolean): Promise<boolean> {
    console.log('🔐 [SUBSCRIPTION-STATUS] Verificando acesso:', { userId, trialExpired });

    try {
      // Se trial ainda está ativo, liberar acesso
      if (!trialExpired) {
        console.log('✅ [SUBSCRIPTION-STATUS] Trial ativo - acesso liberado');
        return true;
      }

      // Trial expirou, verificar subscription Stripe
      const status = await this.checkSubscriptionStatus(userId);

      if (status.hasActiveSubscription) {
        console.log('✅ [SUBSCRIPTION-STATUS] Subscription ativa - acesso liberado');
        return true;
      }

      console.log('🚫 [SUBSCRIPTION-STATUS] Sem trial nem subscription - acesso bloqueado');
      return false;

    } catch (error) {
      console.error('❌ [SUBSCRIPTION-STATUS] Erro ao verificar acesso:', error);
      // Em caso de erro, bloquear acesso por segurança
      return false;
    }
  }

  /**
   * Formata informação de renovação para exibir ao usuário
   */
  getFormattedRenewalInfo(status: SubscriptionStatus): string {
    if (!status.hasActiveSubscription) {
      return 'Nenhuma assinatura ativa';
    }

    if (status.isTrialing) {
      return `Trial ativo - ${status.daysUntilRenewal} dias restantes`;
    }

    if (status.isPastDue) {
      return 'Pagamento atrasado - atualize seu método de pagamento';
    }

    if (status.subscription?.cancel_at_period_end) {
      return `Cancelando em ${status.nextBillingDate?.toLocaleDateString('pt-BR')}`;
    }

    const interval = status.subscription?.price?.recurring.interval === 'year' ? 'anual' : 'mensal';
    return `Renovação ${interval} em ${status.nextBillingDate?.toLocaleDateString('pt-BR')}`;
  }
}

export const subscriptionStatusService = new SubscriptionStatusService();
export default subscriptionStatusService;

